/**
 * Feedback da equipe sobre o próprio Sistema — o botão flutuante no canto da tela.
 *
 * **Não é o feedback dos clientes** (`feedback.ts`, `api/relatorios/feedback`):
 * aquele vem do botão do site novo e mora noutra tabela. Este é quem usa o
 * Sistema dizendo o que quebrou, o que falta e o que ficou bom.
 *
 * As fotos sobem junto com o texto num `multipart/form-data` só. A ApiSistema
 * as grava na ApiConteudo (pasta `feedback/sistema`) **antes** da linha no
 * banco, numa transação: ou entra tudo, ou nada.
 */

import { api, foraDoProjeto, obter } from "./cliente"
import { ErroDaApi } from "./erros"

export type CodigoDoTipo = "problema" | "ideia" | "elogio"

export type TipoDeFeedbackDoSistema = { codigo: CodigoDoTipo; nome: string; descricao: string }

/** Limites da API, repetidos aqui só para a tela avisar antes de enviar. */
export const LIMITES = {
  mensagem: 2000,
  imagens: 5,
  bytesPorImagem: 5 * 1024 * 1024,
} as const

export function listarTiposDoSistema() {
  return obter<TipoDeFeedbackDoSistema[]>("api/feedback-sistema/tipos")
}

export type NovoFeedbackDoSistema = {
  tipo: CodigoDoTipo
  mensagem: string
  /** A rota aberta quando a pessoa escreveu. */
  pagina?: string
  imagens: File[]
}

export type FeedbackEnviado = { id: number; quantidadeImagens: number; mensagem: string }

export async function enviarFeedbackDoSistema(f: NovoFeedbackDoSistema) {
  const corpo = new FormData()
  corpo.append("tipo", f.tipo)
  corpo.append("mensagem", f.mensagem)
  if (f.pagina) corpo.append("pagina", f.pagina)
  for (const img of f.imagens) corpo.append("imagens", img, img.name)

  // Fora de projeto: o feedback não pertence a projeto nenhum. A trava deixa
  // passar esta rota pelo nome — ver `ESCRITAS_DE_RELATO` em `trava.ts`.
  const r = await api.post<FeedbackEnviado>("api/feedback-sistema", corpo, foraDoProjeto())
  return r.data
}

/** Os segundos do 429 ("enviou há pouco"), quando for esse o erro. */
export function segundosParaTentarDeNovo(e: unknown): number | null {
  if (!(e instanceof ErroDaApi) || e.status !== 429) return null
  const s = Number((e.problema as Record<string, unknown> | null)?.segundosRestantes)
  return Number.isFinite(s) && s > 0 ? s : 15
}

// ------------------------------------------------------------------ leitura

export type ImagemDoFeedback = { id: number; tamanhoBytes: number }

export type FeedbackDoSistema = {
  id: number
  tipo: CodigoDoTipo
  tipoNome: string
  mensagem: string
  pagina: string | null
  navegador: string | null
  data: string
  painelId: number
  autor: string
  resolvido: boolean
  resolvidoEm: string | null
  resolvidoPorNome: string | null
  imagens: ImagemDoFeedback[]
}

export type Situacao = "todos" | "abertos" | "resolvidos"

export type PaginaDeFeedbackDoSistema = {
  itens: FeedbackDoSistema[]
  pagina: number
  totalPaginas: number
  totalNoFiltro: number
  contagem: {
    total: number
    abertos: number
    resolvidos: number
    porTipo: Record<CodigoDoTipo, number>
  }
}

export function listarFeedbacksDoSistema(f: {
  tipo?: CodigoDoTipo | ""
  situacao: Situacao
  pagina: number
  /** Texto na mensagem ou na tela de origem — "alguém já pediu isso?". */
  busca?: string
  /** Só os da pessoa logada; as contagens também passam a ser só dela. */
  meus?: boolean
}) {
  return obter<PaginaDeFeedbackDoSistema>("api/feedback-sistema", {
    tipo: f.tipo || undefined,
    situacao: f.situacao,
    pagina: f.pagina,
    busca: f.busca?.trim() || undefined,
    meus: f.meus || undefined,
  })
}

/**
 * Abre o botão flutuante de feedback de qualquer lugar, já com tipo e texto se vierem.
 *
 * É um evento de janela, e não um contexto, porque o botão vive na casca e quem chama
 * (as Solicitações) é uma tela qualquer: nenhum dos dois precisa conhecer o outro.
 */
export const EVENTO_ABRIR_FEEDBACK = "sistema:abrir-feedback"

export function abrirFeedback(inicial?: { tipo?: CodigoDoTipo; texto?: string }) {
  window.dispatchEvent(new CustomEvent(EVENTO_ABRIR_FEEDBACK, { detail: inicial ?? {} }))
}

export type ResultadoDoResolvido = {
  id: number
  resolvido: boolean
  resolvidoEm: string | null
  resolvidoPorNome: string | null
}

export async function marcarFeedbackResolvido(id: number, resolvido: boolean) {
  const r = await api.put<ResultadoDoResolvido>(
    `api/feedback-sistema/${id}/resolvido`, { resolvido }, foraDoProjeto(),
  )
  return r.data
}

/**
 * A foto, como URL de blob para um `<img>`.
 *
 * O `<img src>` não manda o token, e a rota exige token — por isso a foto vem
 * pelo axios. Quem chama revoga a URL quando o componente sai.
 */
export async function urlDaImagemDoFeedback(imagemId: number, largura?: number) {
  const r = await api.get<Blob>(`api/feedback-sistema/imagens/${imagemId}`, {
    params: { largura },
    responseType: "blob",
  })
  return URL.createObjectURL(r.data)
}
